/**
 * Card "newsletter inviata" (o "email automatica", quando la manda un workflow)
 * nella cronologia della scheda contatto.
 *
 * Chiusa: oggetto e stato (consegnata, aperta, click, rimbalzata, disiscritto).
 * Aperta: campagna, date di ogni passaggio, link cliccati e il pulsante che apre
 * l'anteprima dell'email davvero spedita.
 *
 * L'anteprima gira dentro un iframe in scatola chiusa (`sandbox` vuoto): l'HTML
 * di una campagna è contenuto redazionale, non deve poter eseguire nulla né
 * leggere la pagina che lo ospita.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Eye, ExternalLink, Loader2, Mail, X } from 'lucide-react';
import type { ClientNewsletter } from '../../../lib/hooks/useClientNewsletters';
import { useNewsletterPreview } from '../../../lib/hooks/useClientNewsletters';
import { CHIP_NEUTRAL_CLS, CHIP_DONE_CLS, CHIP_LATE_CLS, mediaTileCls } from './cardStyles';
import { CardMedia } from './CardMedia';
import { TimelineFooter } from './TimelineFooter';

interface ClientNewsletterCardProps {
  newsletter: ClientNewsletter;
  formatDate: (dateString: string) => string;
}

/** Riga "etichetta: valore" del blocco aperto. */
function Riga({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-[11px] leading-relaxed">
      <span className="w-[92px] flex-shrink-0 text-[#9B9A97] dark:text-white/40">{label}</span>
      <span className="min-w-0 break-words text-[#37352F] dark:text-gray-300">{value}</span>
    </div>
  );
}

/** Anteprima a schermo intero dell'email spedita. */
function AnteprimaModale({ queueId, oggetto, onClose }: { queueId: string; oggetto: string; onClose: () => void }) {
  const { preview, loading } = useNewsletterPreview(queueId);

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-[#2F3437]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-[rgba(55,53,47,0.09)] px-4 py-3 dark:border-[#3F4447]">
          <Mail className="h-4 w-4 flex-shrink-0 text-[#787774]" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14px] font-medium text-[#37352F] dark:text-white">{oggetto}</div>
            <div className="text-[11px] text-[#9B9A97] dark:text-white/40">Anteprima dell'email spedita</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/10"
            title="Chiudi"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-[#F7F7F5] dark:bg-[#25292B]">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-[#787774] dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carico l'anteprima…
            </div>
          ) : preview?.html ? (
            <iframe
              title={`Anteprima ${oggetto}`}
              srcDoc={preview.html}
              sandbox=""
              className="h-[70vh] w-full border-0 bg-white"
            />
          ) : (
            <div className="py-16 text-center text-[13px] text-[#787774] dark:text-gray-400">
              L'HTML di questa email non è più disponibile
            </div>
          )}
        </div>

        {preview?.clickedUrls.length ? (
          <div className="border-t border-[rgba(55,53,47,0.09)] px-4 py-2.5 dark:border-[#3F4447]">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#787774] dark:text-gray-400">
              Link cliccati
            </div>
            <div className="space-y-0.5">
              {preview.clickedUrls.map((u) => (
                <a
                  key={u}
                  href={u}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-[11px] text-[#2383E2] hover:underline"
                >
                  {u}
                </a>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

export const ClientNewsletterCard: React.FC<ClientNewsletterCardProps> = ({ newsletter, formatDate }) => {
  const [aperto, setAperto] = useState(false);
  const [anteprima, setAnteprima] = useState(false);

  const oggetto = newsletter.subject || newsletter.campaignName || 'Newsletter';
  const fallita = !newsletter.sentAt && (newsletter.status === 'failed' || !!newsletter.errorMessage);

  return (
    <div className="group relative mb-3 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white transition-colors hover:border-[rgba(55,53,47,0.26)] dark:border-[#3F4447] dark:bg-[#2F3437] dark:hover:border-[#565D61]">
      <div className="min-w-0 p-3">
        <button onClick={() => setAperto((v) => !v)} aria-expanded={aperto} className="block w-full text-left">
          <CardMedia
            media={
              <span className={mediaTileCls('email')}>
                <Mail size={15} strokeWidth={1.5} />
              </span>
            }
          >
            <div className="truncate text-sm font-medium leading-snug text-[#37352F] dark:text-white" title={oggetto}>
              {oggetto}
            </div>
            {newsletter.campaignName && (
              <div className="mt-0.5 truncate text-xs text-[#787774] dark:text-white/50">{newsletter.campaignName}</div>
            )}
          </CardMedia>
        </button>

        <TimelineFooter
          tipo={newsletter.source === 'workflow' ? 'Email automatica' : 'Newsletter inviata'}
          accent="email"
          date={newsletter.date}
          titoloData={formatDate(newsletter.date)}
          expanded={aperto}
          left={
            <>
              {newsletter.source === 'workflow' && (
                <Link
                  to={newsletter.workflowId ? `/automation?workflow=${newsletter.workflowId}` : '/automation'}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-[10px] text-[#2383E2] hover:underline"
                  title="Apri l'automazione che l'ha inviata"
                >
                  da automazione{newsletter.workflowName ? ` · ${newsletter.workflowName}` : ''}
                  <ExternalLink className="h-2.5 w-2.5" />
                </Link>
              )}
              {newsletter.clickedAt && <span className={CHIP_DONE_CLS}>Ha cliccato</span>}
              {newsletter.openedAt && <span className={CHIP_DONE_CLS}>Aperta</span>}
              {newsletter.deliveredAt && !newsletter.openedAt && <span className={CHIP_NEUTRAL_CLS}>Consegnata</span>}
              {newsletter.bouncedAt && <span className={CHIP_LATE_CLS}>Rimbalzata</span>}
              {newsletter.unsubscribedAt && <span className={CHIP_LATE_CLS}>Disiscritto</span>}
              {fallita && <span className={CHIP_LATE_CLS}>Non inviata</span>}
              {!newsletter.deliveredAt && !newsletter.bouncedAt && !fallita && newsletter.sentAt && (
                <span className={CHIP_NEUTRAL_CLS}>Inviata</span>
              )}
            </>
          }
        />

        {aperto && (
          <div className="mt-2.5 space-y-2.5">
            <div className="space-y-1 border-t border-[rgba(55,53,47,0.09)] pt-2 dark:border-[#3F4447]">
              {newsletter.sentAt && <Riga label="Inviata" value={formatDate(newsletter.sentAt)} />}
              {newsletter.deliveredAt && <Riga label="Consegnata" value={formatDate(newsletter.deliveredAt)} />}
              {newsletter.openedAt && <Riga label="Aperta" value={formatDate(newsletter.openedAt)} />}
              {newsletter.clickedAt && <Riga label="Click" value={formatDate(newsletter.clickedAt)} />}
              {newsletter.bouncedAt && <Riga label="Rimbalzata" value={formatDate(newsletter.bouncedAt)} />}
              {newsletter.unsubscribedAt && <Riga label="Disiscritto" value={formatDate(newsletter.unsubscribedAt)} />}
              {newsletter.errorMessage && <Riga label="Errore" value={newsletter.errorMessage} />}
            </div>

            <button
              type="button"
              onClick={() => setAnteprima(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-[rgba(55,53,47,0.16)] px-2.5 py-1 text-[11px] text-[#37352F] transition-colors hover:bg-[rgba(55,53,47,0.04)] dark:border-[#3F4447] dark:text-gray-300 dark:hover:bg-white/[0.06]"
            >
              <Eye className="h-3.5 w-3.5" />
              Vedi l'email inviata
              <ExternalLink className="h-3 w-3 opacity-50" />
            </button>
          </div>
        )}
      </div>

      {anteprima && <AnteprimaModale queueId={newsletter.id} oggetto={oggetto} onClose={() => setAnteprima(false)} />}
    </div>
  );
};
